import React, { useEffect, useState } from 'react';
import * as AppPics from 'assets/images';
import { AppTextField, AppButton } from 'components';
import Joi from 'joi';
import { checkForErrors } from 'utilities/fns';
import { useHistory, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { notifier } from 'components/Toaster';
import { useQueryClient } from 'react-query';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import useGetOneSupportCategory from 'hooks/API/GET/useGetOneSupportCategory';
import useSupportTopic from 'hooks/API/POST/useSupportTopic';

function ManageSupportTopic() {
  const { categoryId, topicId } = useParams();

  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const params = new URLSearchParams(location.search);
  const redirectBack = params.get('redirect_back');

  const [topicTitle, setTopicTitle] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [validationErrorServer, setValidationErrorServer] = useState({});

  const { mutate, isLoading } = useSupportTopic();

  const clearForm = () => {
    setTopicTitle('');
    setSelectedType('');
    setFormErrors([]);
    setValidationErrorServer({});
    setDisableSubmit(false);
  };

  const handleGoBack = () => {
    if (redirectBack) {
      history.replace(decodeURIComponent(redirectBack));
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    if (topicId === 'add') {
      clearForm();
      queryClient.removeQueries(['getSingleCategory', topicId]);
      return;
    }
  }, [topicId, queryClient]);

  const getSingleCategory = useGetOneSupportCategory({
    enabled: topicId !== 'add',
    topicId,
    config: { params: { categoryId } },
  });

  useEffect(() => {
    if (topicId !== 'add' && getSingleCategory?.data && getSingleCategory?.isSuccess) {
      const currentData = getSingleCategory.data?.data?.data || getSingleCategory.data?.data;

      setTopicTitle(currentData.title || '');
    }
  }, [topicId, getSingleCategory?.data, getSingleCategory?.isSuccess]);

  const handleTitleChange = (event) => {
    const newValue = event.target.value;
    setTopicTitle(newValue);
  };

  function handleSave() {
    setFormErrors([]);
    if (disableSubmit) return;

    const data = {
      categoryId,
      name: topicTitle.trim(),
      section: selectedType,
      ...(topicId && topicId !== 'add' ? { topicId } : {}),
    };

    const validation = topicSchema.validate(data, { abortEarly: false, allowUnknown: true });
    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    mutate(data, {
      onError(failData) {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message;
        const validationErrors = errorData?.errors;

        setValidationErrorServer(validationErrors || {});
        notifier.error(errorMessage);
      },

      onSuccess(response) {
        const successMessage = response?.data?.data?.message || response?.data?.message;
        notifier.success(successMessage);

        handleGoBack();

        queryClient.invalidateQueries(['support-topic']);
        // queryClient.invalidateQueries(['getSingleCategory']);
      },
    });
  }

  const errors = checkForErrors(formErrors);

  return (
    <>
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleGoBack}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>

          <div
            className="tab__content
          ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Support Category', to: '/support/category' },
                  { label: 'Manage', to: '/support/category/add' },
                ]}
                current={topicId === 'add' ? 'Add Topic' : 'Update Topic'}
              />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="category-form">
                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      {topicId !== 'add' ? (
                        <h2 className="prime-head p-0 boldtitle">Update Topic</h2>
                      ) : (
                        <h2 className="prime-head p-0 boldtitle">Add Topic</h2>
                      )}
                      {validationErrorServer && (
                        <ul className="error-list">
                          {Object.values(validationErrorServer).map((err, idx) => (
                            <li key={idx}>{err.msg}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{ label: 'Title *', htmlFor: 'support-topic' }}
                        inputProps={{
                          placeholder: 'Title',
                          id: 'topicName',
                          name: 'topicName',
                          value: topicTitle,
                          onChange: handleTitleChange,
                          tabIndex: 1,
                        }}
                        fieldErrors={errors.name}
                      />
                      {errors.name && <div className="text-danger">{errors.name.errorMessage}</div>}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    <AppButton
                      type="submit"
                      variantClass="btn-primary mx-3"
                      style={{ minWidth: '1.5rem' }}
                      onClick={handleSave}
                      disabled={disableSubmit || isLoading}
                      tabIndex={6}
                    >
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </AppButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const topicSchema = Joi.object({
  name: Joi.string().min(1).max(100).required().messages({
    'string.base': 'Topic name must be a string.',
    'string.empty': 'Topic name cannot be empty.',
    'string.max': 'Topic name must be at most 100 characters long.',
    'any.required': 'Topic name is required.',
  }),
});

export default ManageSupportTopic;
